import {EndpointCall} from './project-endpoints';

/**
 * Wire format for the Project-mapping endpoints, kept free of any Angular
 * import so `tests/ProjectMappingUrlShape.smoke.ts` can assert it with plain
 * ts-node -- the same arrangement, and for the same reason, as
 * `project-endpoints.ts`.
 *
 * Transcribed from docs/contracts/shared/ProjectMappingReview.md §3. When a
 * route changes there it changes here, and the smoke test is what notices.
 *
 * Note every argument travels in the path or in a JSON body; nothing here is
 * base64, unlike the older Project RPC routes.
 */
export class ProjectMappingEndpoints {
  static readonly ENDPOINT = 'project/mapping';

  static base(apiBaseURL: string): string {
    return `${apiBaseURL}${ProjectMappingEndpoints.ENDPOINT}`;
  }

  /** `GET /project/mapping/dashboard` -- network and serviceProvider only. */
  static dashboard(apiBaseURL: string): EndpointCall {
    return { verb: 'GET', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/dashboard` };
  }

  /** `POST /project/mapping/plan/:orgId` -- enqueued, returns a runId immediately. */
  static plan(apiBaseURL: string, orgId: number): EndpointCall {
    return { verb: 'POST', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/plan/${orgId}` };
  }

  /**
   * `GET /project/mapping/review/:orgId`
   *
   * The single endpoint behind BOTH surfaces. An operator and the customer
   * receive an identical payload, which is what makes a support phone call
   * work -- see the contract §3.3. Do not add a role parameter here.
   */
  static review(apiBaseURL: string, orgId: number): EndpointCall {
    return { verb: 'GET', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/review/${orgId}` };
  }

  /** `POST /project/mapping/decide` -- records answers, writes nothing to `project`. */
  static decide(apiBaseURL: string): EndpointCall {
    return { verb: 'POST', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/decide` };
  }

  /** `POST /project/mapping/apply/:runId` -- enqueued, returns immediately. */
  static apply(apiBaseURL: string, runId: string): EndpointCall {
    return { verb: 'POST', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/apply/${encodeURIComponent(runId)}` };
  }

  /** `GET /project/mapping/run/:runId` -- phase and progress. */
  static run(apiBaseURL: string, runId: string): EndpointCall {
    return { verb: 'GET', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/run/${encodeURIComponent(runId)}` };
  }

  /** `POST /project/mapping/revert/:runId` -- operator only, never offered to an organization. */
  static revert(apiBaseURL: string, runId: string): EndpointCall {
    return { verb: 'POST', url: `${ProjectMappingEndpoints.base(apiBaseURL)}/revert/${encodeURIComponent(runId)}` };
  }
}
